/**
 * Where the sun's shadow of a tree or bush falls: a pure model (no pixi), drawn with the buildings'
 * shadows as one (MapRenderingManager.updateBuildingShadow).
 *
 * A round crown or a bush casts its own silhouette: the art laid flat on the ground under the
 * crown's middle, `depth` as deep as it is drawn tall, and moved along the sun by the crown's
 * height. Its lumps and the see-through gaps between leaf clusters come through, so the shade is
 * leafy and a little dappled rather than a stamped oval. The trunk below the crown throws the
 * people's kind of shadow: a thin line along the sun from its foot. Both are "stamps": the art's
 * own pixels drawn through an affine map, baked with the rest of the shadow.
 *
 * A cone (a spruce) cannot do that: its silhouette laid flat is a triangle pointing north, hidden
 * under the tree, where its real shadow is a pool spreading off to the lower left. A cone, and
 * any plant when silhouettes are turned off, keeps the round-plant model instead: every row of the
 * art is a level slice of the plant, as wide as the row is drawn, at that row's height; a slice's
 * shadow is an ellipse `depth` as deep as it is wide, moved along the sun by its height, as a
 * wall's top is (building_shadow_geometry.ts). All the slices together, kept as their convex hull
 * (offline renders could not tell it from the exact union under the blur), are one polygon.
 *
 * The axis stands where the art's foot is drawn, pulled back into the ground by the foot's own
 * slice: the bottom row of a bush is the FRONT of its base, not its middle.
 */
import { hull, ShadowPolygon } from "./building_shadow_geometry"

/** One row of a plant's art: the span from its first opaque pixel to its last, in art pixels. */
export interface PlantRow { y: number; x0: number; x1: number }

export interface FoliageShadowOptions {
    /** Ground displacement per pixel of height (the people's sun). */
    sun: { x: number; y: number }
    /** How deep a shadow lies on the ground for its width (a slice) or its drawn height (a silhouette). */
    depth: number
    /** False: every plant casts the smooth round-plant outline, cones or not. */
    silhouettes?: boolean
    /** How strongly long grass casts (0: not at all). */
    grass?: number
}

/** A plant's art as its shadow reads it: which pixels are drawn (alpha 255, else 0) and each row's span. */
export interface PlantSilhouette { id: string; width: number; height: number; alpha: Uint8Array; rows: PlantRow[] }

/** Alpha above `threshold` counts as drawn. */
export const plantSilhouette = (id: string, rgba: ArrayLike<number>, width: number, height: number, threshold = 128): PlantSilhouette => {
    const alpha = new Uint8Array(width * height)
    for (let i = 0; i < alpha.length; i++) if (rgba[i * 4 + 3] > threshold) alpha[i] = 255
    return { id, width, height, alpha, rows: plantRows(rgba, width, height, threshold) }
}

/** Each drawn row's span, top to bottom. */
export const plantRows = (rgba: ArrayLike<number>, width: number, height: number, threshold = 128): PlantRow[] => {
    const rows: PlantRow[] = []
    for (let y = 0; y < height; y++) {
        let x0 = -1, x1 = -1
        for (let x = 0; x < width; x++) {
            if (rgba[(y * width + x) * 4 + 3] <= threshold) continue
            if (x0 < 0) x0 = x
            x1 = x + 1
        }
        if (x0 >= 0) rows.push({ y, x0, x1 })
    }
    return rows
}

/** Where a plant's parts are, in art rows. */
export interface PlantAnatomy {
    /** One past the last drawn row. */
    base: number
    /** Where the plant's upright axis meets the ground (see the module comment). */
    axis: number
    /** The crown: its first and last rows, and its middle (rows weighted by their width). */
    top: number
    crownBottom: number
    crownMiddle: number
    /** Widest at the bottom of its crown and narrow a quarter of the way down: a spruce. */
    cone: boolean
}

/** A trunk row is no wider than this share of the plant's widest row. */
const TRUNK_SHARE = 0.35

export const plantAnatomy = (rows: readonly PlantRow[], depth: number): PlantAnatomy => {
    const base = rows[rows.length - 1].y + 1
    const width = (row: PlantRow) => row.x1 - row.x0
    const foot = rows.filter(row => row.y >= base - 3).reduce((widest, row) => Math.max(widest, width(row)), 0)
    const widest = rows.reduce((max, row) => Math.max(max, width(row)), 0)
    // The trunk: rows up from the foot while they stay narrow. Everything above is crown.
    let bottom = rows.length - 1
    while (bottom > 0 && width(rows[bottom]) <= TRUNK_SHARE * widest) bottom--
    const crown = rows.slice(0, bottom + 1)
    const top = crown[0].y, crownBottom = crown[crown.length - 1].y
    const weight = crown.reduce((sum, row) => sum + width(row), 0)
    const crownMiddle = crown.reduce((sum, row) => sum + (row.y + 0.5) * width(row), 0) / weight
    const widestRow = crown.reduce((best, row) => (width(row) > width(best) ? row : best))
    const quarter = crown.find(row => row.y >= top + (crownBottom - top) / 4) ?? widestRow
    const span = Math.max(1, crownBottom - top)
    return {
        base, axis: base - (depth * foot) / 2, top, crownBottom, crownMiddle,
        cone: (widestRow.y - top) / span > 0.75 && width(quarter) < 0.5 * width(widestRow),
    }
}

/** An affine map as pixi's Matrix holds it: x' = a x + c y + tx, y' = b x + d y + ty. */
export type StampMatrix = [number, number, number, number, number, number]

/**
 * Rows `y`..`y + height` of a plant's art, drawn black through `matrix` (from those rows' own pixels).
 * `strength` < 1 draws it partly see-through (long grass): overlapping ones build up, and over a full
 * shadow it changes nothing.
 */
export interface ShadowStamp { art: PlantSilhouette; y: number; height: number; matrix: StampMatrix; strength?: number }

/** What a plant casts, in its art's own pixels (the art's top-left is the origin). */
export interface PlantShadowParts { polygons: ShadowPolygon[]; stamps: ShadowStamp[]; foot?: ShadowPolygon }

/** Points around an ellipse, enough that the hull's facets stay within a pixel of it. */
const SEGMENTS = 16

/** The round-plant outline: every row a slice, their shadows' convex hull. [] for empty art. */
export const plantShadow = (rows: readonly PlantRow[], { sun, depth }: FoliageShadowOptions): ShadowPolygon => {
    if (!rows.length) return []
    const { axis } = plantAnatomy(rows, depth)
    const points: number[] = []
    rows.forEach(({ y, x0, x1 }) => {
        const h = Math.max(0, axis - (y + 0.5))
        const cx = (x0 + x1) / 2 + sun.x * h, cy = axis + sun.y * h
        const rx = (x1 - x0) / 2, ry = depth * rx
        for (let i = 0; i < SEGMENTS; i++) {
            const a = (i / SEGMENTS) * Math.PI * 2
            points.push(cx + rx * Math.cos(a), cy + ry * Math.sin(a))
        }
    })
    return hull(points)
}

export const plantShadowParts = (art: PlantSilhouette, options: FoliageShadowOptions): PlantShadowParts => {
    const { rows } = art
    if (!rows.length) return { polygons: [], stamps: [] }
    const { sun, depth } = options
    const { base, axis, top, crownBottom, crownMiddle, cone } = plantAnatomy(rows, depth)
    const foot = plantFoot(rows, depth)
    if (cone || options.silhouettes === false) return { polygons: [plantShadow(rows, options)], stamps: [], foot }
    // The crown laid flat with its middle over the axis, then moved by the sun as far as its middle is high.
    const lift = axis - crownMiddle
    const stamps: ShadowStamp[] = [{ art, y: top, height: crownBottom - top + 1,
        matrix: [1, 0, 0, depth, sun.x * lift, axis + depth * (top - crownMiddle) + sun.y * lift] }]
    // Each trunk pixel moved by the sun as far as it is high.
    const trunk = crownBottom + 1
    if (base > trunk) stamps.push({ art, y: trunk, height: base - trunk,
        matrix: [1, 0, -sun.x, -sun.y, sun.x * (axis - trunk), axis + sun.y * (axis - trunk)] })
    return { polygons: [], stamps, foot }
}

/**
 * Where a plant meets the ground, for its contact shade: an ellipse under its foot, as wide as its
 * bottom rows are drawn (plus `spread` each side) and `depth` as deep, centred on the axis. [] for empty art.
 */
export const plantFoot = (rows: readonly PlantRow[], depth: number, spread = 2): ShadowPolygon => {
    if (!rows.length) return []
    const { base, axis } = plantAnatomy(rows, depth)
    const foot = rows.filter(row => row.y >= base - 3)
    const x0 = Math.min(...foot.map(row => row.x0)), x1 = Math.max(...foot.map(row => row.x1))
    const rx = (x1 - x0) / 2 + spread, ry = Math.max(2, depth * rx)
    const cx = (x0 + x1) / 2
    const points: number[] = []
    for (let i = 0; i < SEGMENTS; i++) {
        const a = (i / SEGMENTS) * Math.PI * 2
        points.push(cx + rx * Math.cos(a), axis + ry * Math.sin(a))
    }
    return points
}

/**
 * What an upright thing that is not a plant casts (a boulder, a barrel, a statue, a tent, a dead tree):
 * its whole silhouette stood up from its foot and sheared along the sun, every row moved as far as it is
 * high -- the people's shadow, from the art's bottom row. Plus its foot, for contact shade.
 */
export const uprightShadowParts = (art: PlantSilhouette, options: FoliageShadowOptions, strength = 1): PlantShadowParts => {
    const { rows } = art
    if (!rows.length) return { polygons: [], stamps: [] }
    const { sun, depth } = options
    const top = rows[0].y, base = rows[rows.length - 1].y + 1
    return {
        polygons: [],
        stamps: [{ art, y: top, height: base - top, ...(strength < 1 ? { strength } : {}),
            matrix: [1, 0, -sun.x, -sun.y, sun.x * (base - top), base + sun.y * (base - top)] }],
        ...(strength < 1 ? {} : { foot: plantFoot(rows, depth) }),
    }
}

/**
 * How a composite tile casts: a plant's crown and trunk, an upright silhouette, long grass (an upright
 * silhouette, lighter, drawn but never asked about -- sprite_shade.ts), or nothing.
 */
export type TileShadowModel = "foliage" | "upright" | "grass" | "none"

/** Art whose share of half-transparent pixels is at least this paints its own ground shadow (the LPC trees, bushes, palms). */
export const BAKED_SHADOW_SHARE = 0.1

/**
 * How a composite casts, from its tile and its art. `castsShadow` plants keep the foliage model; long
 * grass (drawn as dense vertical copies) casts as grass. Nothing is cast by microtile copies (`dense`), art
 * that lies flat (sorted well under its own row, like a campfire's ring), art that paints its own ground
 * shadow, or art too small to stand. Everything else stands up.
 */
export const tileShadowModel = (tile: { castsShadow?: boolean; tileTypes?: readonly string[]; renderZIdxOffset?: number },
    rgba: ArrayLike<number>, width: number, height: number): TileShadowModel => {
    if (tile.castsShadow) return "foliage"
    if (tile.tileTypes?.includes("denseVertical")) return "grass"
    if (tile.tileTypes?.includes("dense")) return "none"
    if ((tile.renderZIdxOffset ?? 0) <= -16) return "none"
    let drawn = 0, soft = 0, minY = height, maxY = -1
    for (let i = 0; i < width * height; i++) {
        const a = rgba[i * 4 + 3]
        if (!a) continue
        drawn++
        if (a < 200) soft++
        if (a > 128) {
            const y = Math.floor(i / width)
            minY = Math.min(minY, y); maxY = Math.max(maxY, y)
        }
    }
    if (!drawn || soft / drawn >= BAKED_SHADOW_SHARE) return "none"
    return maxY - minY + 1 >= 6 ? "upright" : "none"
}

/** A polygon moved by (dx, dy). */
export const translatePolygon = (polygon: ShadowPolygon, dx: number, dy: number): ShadowPolygon =>
    polygon.map((v, i) => v + (i % 2 ? dy : dx))

/** A stamp moved by (dx, dy). */
export const translateStamp = (stamp: ShadowStamp, dx: number, dy: number): ShadowStamp => {
    const [a, b, c, d, tx, ty] = stamp.matrix
    return { ...stamp, matrix: [a, b, c, d, tx + dx, ty + dy] }
}

/** The quadrilateral a stamp's rows land on. */
export const stampQuad = ({ art, height, matrix: [a, b, c, d, tx, ty] }: ShadowStamp): ShadowPolygon => {
    const w = art.width
    return [tx, ty, a * w + tx, b * w + ty, a * w + c * height + tx, b * w + d * height + ty, c * height + tx, d * height + ty]
}

/** Whether a stamp darkens the point (x, y): it lands on one of its art's drawn pixels. */
export const stampCovers = ({ art, y, height, matrix: [a, b, c, d, tx, ty] }: ShadowStamp, px: number, py: number): boolean => {
    const det = a * d - b * c
    if (!det) return false
    const u = Math.floor((d * (px - tx) - c * (py - ty)) / det)
    const v = Math.floor((a * (py - ty) - b * (px - tx)) / det)
    if (u < 0 || v < 0 || u >= art.width || v >= height) return false
    return art.alpha[(y + v) * art.width + u] > 0
}

/** A key for a set of stamps that changes when any moves by a quarter pixel or draws other rows or art. */
export const stampsKey = (stamps: readonly ShadowStamp[]): string => {
    let a = 0x811c9dc5, b = 0x9e3779b9
    const mix = (v: number) => {
        a = Math.imul(a ^ v, 0x01000193)
        b = Math.imul(b ^ (v + 0x632be5ab), 0x85ebca6b) ^ (b >>> 13)
    }
    for (const { art, y, height, matrix, strength } of stamps) {
        for (let i = 0; i < art.id.length; i++) mix(art.id.charCodeAt(i))
        mix(y); mix(height); mix(Math.round((strength ?? 1) * 100))
        for (const v of matrix) mix(Math.round(v * 4))
    }
    return `${stamps.length}:${a >>> 0}:${b >>> 0}`
}
